import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { WORKSPACE_SLUG_KEY, clearStayOnLanding } from '../../config/sessionKeys';
import LoginShell from './LoginShell';
import useNoIndex from './useNoIndex';

// Step 2 of the WT-style login: branded sign-in for a specific workspace. Resolves
// the workspace name by slug (public), then hands off to the identity provider
// (MSAL/Entra). The provider is intentionally not named on-screen — users sign in
// with their work account; which service brokers it is not their concern.
export default function TenantLogin({ onSignIn, isLoading, error }) {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [org, setOrg] = useState(null);
    const errorRef = useRef(error);
    errorRef.current = error;
    // 'loading' | 'found' | 'notFound' (404 only) | 'unreachable' (network, 5xx: QA L-8)
    const [lookup, setLookup] = useState('loading');
    const [attempt, setAttempt] = useState(0);
    const lookupKeyRef = useRef({ slug, attempt: 0 });
    // The sign-in error (AuthContext) as it stood when this workspace's lookup
    // started: an old failure is not shown under a new workspace or after a
    // retry (QA L-15). A new attempt resets the error first, so a fresh failure
    // shows again.
    const [staleError, setStaleError] = useState(null);
    useEffect(() => { if (!error) setStaleError(null); }, [error]);
    const showError = Boolean(error) && error !== staleError;
    // A dealership's sign-in address is not for search results (both designs).
    useNoIndex();

    useEffect(() => {
        let alive = true;
        try { sessionStorage.setItem(WORKSPACE_SLUG_KEY, slug); } catch (e) { /* ignore */ }
        setLookup('loading');
        // Only when the workspace or the attempt really changed: the screen
        // remounts after a failed popup (App shows its spinner meanwhile) and
        // StrictMode re-runs effects; that failure must still show.
        const prev = lookupKeyRef.current;
        if (prev.slug !== slug || prev.attempt !== attempt) setStaleError(errorRef.current);
        lookupKeyRef.current = { slug, attempt };
        apiService.getPublicOrg(slug)
            .then((o) => { if (alive) { setOrg(o); setLookup('found'); } })
            .catch((err) => { if (alive) setLookup(err?.status === 404 ? 'notFound' : 'unreachable'); });
        return () => { alive = false; };
    }, [slug, attempt]);

    // The provider's popup can be closed or fail; AuthContext has already set
    // (or, for a cancel, deliberately not set) the message. Catch it here so it
    // never surfaces as an uncaught rejection (QA L-1).
    const signIn = () => {
        clearStayOnLanding();
        const pending = onSignIn();
        if (pending && typeof pending.catch === 'function') {
            pending.catch(() => { /* message (or quiet cancel) handled in AuthContext */ });
        }
    };

    if (lookup === 'notFound') {
        return (
            <LoginShell>
                <span className="lg-eyebrow">Workspace</span>
                <h1 className="lg-title">Workspace not found</h1>
                <p className="lg-sub">We couldn’t find <b>{slug}</b>. Check the address and try again.</p>
                <button className="lg-btn" onClick={() => navigate('/login')}>← Try another workspace</button>
            </LoginShell>
        );
    }

    if (lookup === 'unreachable') {
        return (
            <LoginShell>
                <span className="lg-workspace-chip">{slug}</span>
                <h1 className="lg-title">Sign in to {slug}</h1>
                <p className="lg-error" role="alert">We couldn’t reach IronSight. Try again.</p>
                <button className="lg-btn lg-btn-retry" onClick={() => setAttempt((n) => n + 1)}>Retry</button>
                <button className="lg-link" onClick={() => navigate('/login')}>← Different workspace</button>
            </LoginShell>
        );
    }

    return (
        <LoginShell>
            <span className="lg-workspace-chip">{org?.name || slug}</span>
            <h1 className="lg-title">Sign in to {org?.name || slug}</h1>
            <button className="lg-btn" onClick={signIn} disabled={isLoading}>
                {isLoading ? 'Signing in…' : 'Sign in to IronSight'}
            </button>
            {showError && <p className="lg-error" role="alert">Sign-in didn’t go through. Please try again.</p>}
            <button className="lg-link" onClick={() => navigate('/login')}>← Different workspace</button>
        </LoginShell>
    );
}

TenantLogin.propTypes = {
    onSignIn: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.object,
};
